import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { BackdropLoader, SelectMenu, toast } from '@livable/saas-react-components';
import { Paper, Typography, TextField, Grid, ToggleButtonGroup, ToggleButton, MenuItem, Button } from '@mui/material';
import { useFormik } from 'formik';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DetailedStreetFinder } from 'src/components/general/DetailedStreetFinder';
import { useStore } from 'src/stores';
import { companyDetailsInitialValues, companyDetailsSchema } from 'src/utils/formValidation/Company';
import useStyles from './styles';
import { softwareList } from 'src/utils/company';
import { USER_ROLES_BY_NAME } from 'src/constants/userSetting';
import { api } from 'src/api';
import { SimpleConfirmationModal } from 'src/components/shared/SimpleConfirmationModal';
import { isLivableStaffFromToken } from 'src/utils/isLivableStaffFromToken';
import { convergentErrorKey, getConvergentFieldState } from './convergentGate';
export const CompanyDetailsTab = observer(() => {
  const classes = useStyles();
  const store = useStore();
  const _useTranslation = useTranslation(),
    t = _useTranslation.t;
  const _useState = useState('no'),
    _useState2 = _slicedToArray(_useState, 2),
    additionalSoftware = _useState2[0],
    setAdditional = _useState2[1];
  const _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    handleStartValidate = _useState4[0],
    setHandleStartValidate = _useState4[1];
  const _useState5 = useState({}),
    _useState6 = _slicedToArray(_useState5, 2),
    defaultPropertyAddress = _useState6[0],
    setDefaultPropertyAddress = _useState6[1];
  const _useState7 = useState(false),
    _useState8 = _slicedToArray(_useState7, 2),
    isLoading = _useState8[0],
    setIsLoading = _useState8[1];
  const _useState9 = useState(false),
    _useState0 = _slicedToArray(_useState9, 2),
    isConvergentConfirmOpen = _useState0[0],
    setIsConvergentConfirmOpen = _useState0[1];
  const _useState1 = useState(false),
    _useState10 = _slicedToArray(_useState1, 2),
    isSavingConvergent = _useState10[0],
    setIsSavingConvergent = _useState10[1];
  const formik = useFormik({
    initialValues: companyDetailsInitialValues,
    validationSchema: companyDetailsSchema,
    onSubmit: async () => {
      setHandleStartValidate(true);
    }
  });
  const getCurrentUserCompanyFromToken = async () => {
    const resp = await store.company.getCurrentUserCompanyFromTokenAsync.run();
    return resp;
  };
  const handleConvergentConfirm = async _ref => {
    let action = _ref.action;
    if (isSavingConvergent) return;
    if (action !== 'submit') {
      setIsConvergentConfirmOpen(false);
      return;
    }
    setIsSavingConvergent(true);
    try {
      // Called directly rather than via createAsync: its failure handler toasts a generic
      // server error, which would stack on top of the mapped message below.
      await api.Companies.setConvergentBilling(formik.values.id, {
        cancelToken: {}
      });
      store.company.companyFeatureStore.setFeatureFlags({
        convergentBillingMode: true
      });
      toast('success', {
        body: [t('company_details.convergent.success')]
      });
    } catch (err) {
      var _err$response$data$Er, _err$response, _err$response2;
      const code = (_err$response$data$Er = err === null || err === void 0 || (_err$response = err.response) === null || _err$response === void 0 || (_err$response = _err$response.data) === null || _err$response === void 0 || (_err$response = _err$response.Error) === null || _err$response === void 0 ? void 0 : _err$response.ErrorCode) !== null && _err$response$data$Er !== void 0 ? _err$response$data$Er : err === null || err === void 0 || (_err$response2 = err.response) === null || _err$response2 === void 0 || (_err$response2 = _err$response2.data) === null || _err$response2 === void 0 || (_err$response2 = _err$response2.error) === null || _err$response2 === void 0 ? void 0 : _err$response2.errorCode;
      toast('error', {
        body: [t(convergentErrorKey(code))]
      });
    } finally {
      setIsSavingConvergent(false);
      setIsConvergentConfirmOpen(false);
    }
  };
  const convergentField = getConvergentFieldState({
    companyId: formik.values.id,
    flagEnabled: store.account.featureFlagsStore.getIsConvergentBillingEnabled,
    isLivableAdmin: isLivableStaffFromToken(),
    isConvergent: store.company.companyFeatureStore.convergentBillingMode
  });
  useEffect(() => {
    setIsLoading(true);
    getCurrentUserCompanyFromToken().then(async resp => {
      if (resp && resp.isSuccessful) {
        const _resp$response = resp.response,
          companyType = _resp$response.companyType,
          id = _resp$response.id,
          name = _resp$response.name,
          propertyManagementSoftware = _resp$response.propertyManagementSoftware,
          unitsProjected = _resp$response.unitsProjected,
          website = _resp$response.website,
          addressId = _resp$response.addressId;
        if (addressId) {
          const addressResp = await store.address.batchGetAddressesAsync.run({
            addressIds: [addressId]
          });
          if (addressResp && addressResp.isSuccessful) {
            var _addressResp$response;
            setDefaultPropertyAddress(addressResp.response[0]);
            formik.setFieldValue('postalAddress', "".concat(addressResp.response[0].street, " ").concat(addressResp.response[0].city, ", ").concat(addressResp.response[0].state, " ").concat(addressResp.response[0].zip).concat((_addressResp$response = addressResp.response[0]) !== null && _addressResp$response !== void 0 && _addressResp$response.secondary ? ', ' + addressResp.response[0].secondary : ''));
          }
        }
        formik.setFieldValue('companyType', companyType);
        formik.setFieldValue('id', id);
        formik.setFieldValue('name', name);
        formik.setFieldValue('unitsProjected', unitsProjected);
        formik.setFieldValue('website', website);
        if (propertyManagementSoftware && !softwareList.includes(propertyManagementSoftware)) {
          setAdditional('yes');
          formik.setFieldValue('propertyManagementSoftware', 'other');
          formik.setFieldValue('specifySoftware', propertyManagementSoftware);
        } else if (softwareList.includes(propertyManagementSoftware)) {
          setAdditional('yes');
          formik.setFieldValue('propertyManagementSoftware', propertyManagementSoftware);
        }
      }
    }).finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // const [ownerType, setOwnerType] = useState('independent');
  // const handleOwnerTypeChange = event => {
  //   if (event.target.value) setOwnerType(event.target.value);
  // };

  const handleAdditionalChange = event => {
    if (event.target.value) setAdditional(event.target.value);
  };
  const handleStartSubmitting = async resultId => {
    const data = _objectSpread({
      companyType: 'IndependentRentalOwner'
    }, formik.values);
    if (resultId) {
      data.addressId = resultId;
    }
    if (additionalSoftware === 'yes' && formik.values.propertyManagementSoftware === 'other') {
      data.propertyManagementSoftware = formik.values.specifySoftware;
    } else if (additionalSoftware === 'yes') {
      data.propertyManagementSoftware = formik.values.propertyManagementSoftware;
    } else {
      data.propertyManagementSoftware = '';
    }
    const resp = await store.company.updateCompanyAsync.run(data);
    if (resp && resp.isSuccessful) {
      toast('success', {
        body: [t('company_details.company_updated')]
      });
    }
  };
  return /*#__PURE__*/React.createElement("div", {
    className: classes.tabWrapper
  }, /*#__PURE__*/React.createElement(Paper, null, /*#__PURE__*/React.createElement("form", {
    onSubmit: formik.handleSubmit
  }, /*#__PURE__*/React.createElement(BackdropLoader, {
    isOpen: isLoading
  }), /*#__PURE__*/React.createElement("div", {
    className: classes.paperBody
  }, /*#__PURE__*/React.createElement(Grid, {
    container: true,
    rowSpacing: 24
  }, /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h4"
  }, t('general.edit'), " ", t('company_details.title').toLowerCase())), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12
  }, /*#__PURE__*/React.createElement(TextField, {
    error: formik.touched.name && Boolean(formik.errors.name),
    fullWidth: true,
    helperText: formik.touched.name && t(formik.errors.name, {
      field: t('general.business_name')
    }),
    id: "name",
    label: t('company_details.enter_business_name'),
    name: "name",
    onBlur: formik.handleBlur,
    onChange: formik.handleChange,
    value: formik.values.name,
    variant: "outlined",
    disabled: store.company.companyFeatureStore.isAppfolio && !store.account.featureFlagsStore.getIsAppfolioPaperStatementFeeEnabled || store.account.userProfile.userRole === USER_ROLES_BY_NAME.PropertyManagerRegular
  })), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h4"
  }, t('company_details.address'))), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12
  }, /*#__PURE__*/React.createElement(DetailedStreetFinder, {
    _serviceVariableValidate: handleStartValidate,
    _serviceStopValidate: () => setHandleStartValidate(false),
    _serviceStartSubmitting: handleStartSubmitting,
    defaultPropertyAddress: defaultPropertyAddress,
    error: formik.touched.postalAddress && Boolean(formik.errors.postalAddress),
    helperText: formik.touched.postalAddress && t(formik.errors.postalAddress, {
      field: t('company_details.corporate_postal_mailing_address')
    }),
    name: "postalAddress",
    validator: formik,
    disabled: store.company.companyFeatureStore.isAppfolio && !store.account.featureFlagsStore.getIsAppfolioPaperStatementFeeEnabled || store.account.userProfile.userRole === USER_ROLES_BY_NAME.PropertyManagerRegular
  }))), /*#__PURE__*/React.createElement("div", {
    className: classes.mdSection
  }, /*#__PURE__*/React.createElement(Grid, {
    container: true,
    rowSpacing: 24
  }, /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h4"
  }, t('company_details.how_many_units'))), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 5
  }, /*#__PURE__*/React.createElement(TextField, {
    error: formik.touched.unitsProjected && Boolean(formik.errors.unitsProjected),
    fullWidth: true,
    helperText: formik.touched.unitsProjected && t(formik.errors.unitsProjected),
    id: "unitsProjected",
    label: t('company_details.number_of_units_optional'),
    name: "unitsProjected",
    onBlur: formik.handleBlur,
    onChange: e => {
      if (!/^[1-9][0-9]*$|^$/.test(e.target.value)) return;
      formik.handleChange(e);
    },
    onKeyPress: event => {
      if (!/[0-9]/.test(event.key)) {
        event.preventDefault();
      }
    },
    value: formik.values.unitsProjected,
    variant: "outlined",
    type: "number",
    disabled: store.company.companyFeatureStore.isAppfolio && !store.account.featureFlagsStore.getIsAppfolioPaperStatementFeeEnabled || store.account.userProfile.userRole === USER_ROLES_BY_NAME.PropertyManagerRegular
  })))), /*#__PURE__*/React.createElement("div", {
    className: classes.mdSection
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h4"
  }, t('company_details.property_management_software'))), /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.flexContainer, " ").concat(classes.smSection)
  }, /*#__PURE__*/React.createElement(ToggleButtonGroup, {
    exclusive: true,
    onChange: handleAdditionalChange,
    value: additionalSoftware,
    disabled: store.company.companyFeatureStore.isAppfolio && !store.account.featureFlagsStore.getIsAppfolioPaperStatementFeeEnabled || store.account.userProfile.userRole === USER_ROLES_BY_NAME.PropertyManagerRegular,
    'custom-attribute': '729-company-details-page'
  }, /*#__PURE__*/React.createElement(ToggleButton, {
    value: "no",
    'custom-attribute': '730-company-details-page-no'
  }, t('general.no')), /*#__PURE__*/React.createElement(ToggleButton, {
    value: "yes",
    'custom-attribute': '730-company-details-page-yes'
  }, t('general.yes'))), additionalSoftware === 'yes' && /*#__PURE__*/React.createElement("div", {
    className: classes.inlineSelect
  }, /*#__PURE__*/React.createElement(SelectMenu, {
    error: formik.touched.propertyManagementSoftware && Boolean(formik.errors.propertyManagementSoftware),
    helperText: formik.touched.propertyManagementSoftware && t(formik.errors.propertyManagementSoftware, {
      field: t('company_details.property_management_software')
    }),
    id: "propertyManagementSoftware",
    label: t('general.please_select'),
    labelId: "software-selector-label",
    name: "propertyManagementSoftware",
    onChange: formik.handleChange,
    value: formik.values.propertyManagementSoftware,
    disabled: store.company.companyFeatureStore.isAppfolio && !store.account.featureFlagsStore.getIsAppfolioPaperStatementFeeEnabled || store.account.userProfile.userRole === USER_ROLES_BY_NAME.PropertyManagerRegular,
    'custom-attribute': '728-company-details-page'
  }, softwareList.map(s => /*#__PURE__*/React.createElement(MenuItem, {
    value: s,
    key: s
  }, s)), /*#__PURE__*/React.createElement(MenuItem, {
    value: "other"
  }, t('general.other')))), additionalSoftware === 'yes' && formik.values.propertyManagementSoftware === 'other' && /*#__PURE__*/React.createElement(TextField, {
    error: formik.touched.specifySoftware && Boolean(formik.errors.specifySoftware),
    fullWidth: true,
    helperText: formik.touched.specifySoftware && t(formik.errors.specifySoftware),
    id: "specifySoftware",
    label: t('general.specify_optional'),
    name: "specifySoftware",
    onBlur: formik.handleBlur,
    onChange: formik.handleChange,
    value: formik.values.specifySoftware,
    variant: "outlined",
    disabled: store.company.companyFeatureStore.isAppfolio && !store.account.featureFlagsStore.getIsAppfolioPaperStatementFeeEnabled || store.account.userProfile.userRole === USER_ROLES_BY_NAME.PropertyManagerRegular
  })), convergentField.show && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: classes.mdSection
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h4"
  }, t(convergentField.labelKey))), /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.flexContainer, " ").concat(classes.smSection)
  }, /*#__PURE__*/React.createElement(ToggleButtonGroup, {
    exclusive: true,
    value: convergentField.locked ? 'yes' : 'no',
    disabled: convergentField.locked,
    onChange: (event, value) => {
      if (value === 'yes') setIsConvergentConfirmOpen(true);
    },
    'custom-attribute': '731-company-details-page-convergent'
  }, /*#__PURE__*/React.createElement(ToggleButton, {
    value: "no",
    'custom-attribute': '732-company-details-page-convergent-no'
  }, t('general.no')), /*#__PURE__*/React.createElement(ToggleButton, {
    value: "yes",
    'custom-attribute': '732-company-details-page-convergent-yes'
  }, t('general.yes')))), convergentField.showExplanation && /*#__PURE__*/React.createElement("div", {
    className: classes.smSection
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body3",
    component: "div"
  }, t(convergentField.explanationKey))))), store.account.userProfile.userRole !== USER_ROLES_BY_NAME.PropertyManagerRegular && /*#__PURE__*/React.createElement("div", {
    className: classes.pageFooter
  }, /*#__PURE__*/React.createElement(Button, {
    type: "submit",
    variant: "priority",
    disabled: store.company.companyFeatureStore.isAppfolio && !store.account.featureFlagsStore.getIsAppfolioPaperStatementFeeEnabled,
    'custom-attribute': '727-company-details-page'
  }, t('buttons.save_changes'))))), /*#__PURE__*/React.createElement(SimpleConfirmationModal, {
    headerText: t('company_details.convergent.confirm_title'),
    bodyText: t('company_details.convergent.confirm_body', {
      companyName: formik.values.name
    }),
    externalIsOpen: isConvergentConfirmOpen,
    isLoading: isSavingConvergent,
    customSubmitButtonName: t('company_details.convergent.confirm_button'),
    callBack: handleConvergentConfirm
  }));
});